/* eslint-disable react/display-name */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  OrgAnalyticsContext,
  Period,
  TeamAnalyticsDimensionType
} from 'graphql/types';
import {
  encodeTeamDataId,
  useGetTeamAnalyticsData
} from 'modules/Insights/Notes/hooks/useGetTeamAnalyticsData';
import { useGetSearchLabels } from 'modules/Insights/hooks/useGetSearchLabels';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';
import { useGetTeamAnalyticsId } from 'modules/Insights/Notes/hooks/useGetTeamAnalyticsId';
import { transformAnalyticsToBillable } from './utils';
import TeamTable from './TeamTable';
import TeamTableLoading from './TeamTableLoading';
import BillableHoursCell from './BillableHoursCell';

type BillableUtilizationProp = {
  columns: string[];
  previousPeriod: React.MutableRefObject<Period | null>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const BillableUtilization: React.FC<BillableUtilizationProp> = ({
  columns,
  previousPeriod,
  setLoading
}: BillableUtilizationProp) => {
  const pageNumberRef = useRef(1);
  const labelValues = useGetSearchLabels();

  const { currentPeriod } = useSupervisorInsightContext();

  const [updatedData, setUpdatedData] = useState<any[]>([]);
  const [moreLoading, setMoreLoading] = useState(false);
  useEffect(() => {
    if (
      (previousPeriod.current &&
        (previousPeriod?.current.startDate !== currentPeriod?.startDate ||
          previousPeriod?.current.endDate !== currentPeriod?.endDate)) ||
      labelValues
    ) {
      setUpdatedData([]);
      pageNumberRef.current = 1;
      previousPeriod.current = currentPeriod;
    }
  }, [
    currentPeriod?.startDate,
    currentPeriod?.endDate,
    currentPeriod,
    previousPeriod,
    labelValues
  ]);

  const { data: teamId } = useGetTeamAnalyticsId({
    teamContext: OrgAnalyticsContext.Supervisor,
    teamDimensionType:
      TeamAnalyticsDimensionType.MetricsByTeamBillableUtilization,
    labels: labelValues,
    pageNumber: 1,
    pageSize: 30
  });

  const {
    data: teamData,
    refetch,
    isEmpty,
    loading: dataloading
  } = useGetTeamAnalyticsData(teamId);

  const dataSet = useMemo(() => {
    if (isEmpty) return [];
    else {
      return transformAnalyticsToBillable(
        teamData?.orgAnalytics?.teamAnalyticsResult.team,
        currentPeriod
      );
    }
  }, [
    currentPeriod,
    isEmpty,
    teamData?.orgAnalytics?.teamAnalyticsResult.team
  ]);

  const loadMore = useCallback(async () => {
    if (teamData?.orgAnalytics?.teamAnalyticsResult.hasMoreData === true) {
      setMoreLoading(true);
      pageNumberRef.current += 1;
      const id = encodeTeamDataId({
        dimensionValue:
          TeamAnalyticsDimensionType.MetricsByTeamBillableUtilization,
        contextValue: OrgAnalyticsContext.Supervisor,
        labels: labelValues,
        pageNumber: pageNumberRef.current,
        pageSize: 10,
        dateRangeValue: {
          startDate: currentPeriod?.startDate || '',
          endDate: currentPeriod?.endDate || ''
        }
      });

      await refetch({ teamAnalyticsResultId: id });
      setMoreLoading(false);
    }
  }, [
    currentPeriod?.endDate,
    currentPeriod?.startDate,
    labelValues,
    refetch,
    teamData?.orgAnalytics?.teamAnalyticsResult.hasMoreData
  ]);

  const loadingState = useMemo(() => {
    const isLoading =
      (dataloading && updatedData.length === 0) || !teamId || teamId === '';
    setLoading(isLoading);
    return isLoading;
  }, [dataloading, setLoading, teamId, updatedData.length]);

  useEffect(() => {
    if (teamData) {
      setUpdatedData((previous) => {
        return [...previous, ...dataSet];
      });
    }
  }, [dataSet, teamData]);

  const cellComponent = useCallback((info) => {
    return (
      <BillableHoursCell
        billableDuration={info.billableHours || 0}
        scheduledDuration={info.scheduleHours || 0}
      />
    );
  }, []);

  return (
    <>
      {loadingState ? (
        <TeamTableLoading columns={columns} />
      ) : (
        <TeamTable
          columns={columns}
          data={updatedData}
          loadMore={loadMore}
          moreLoading={moreLoading}
          isEmpty={isEmpty}
          cellComponent={cellComponent}
        />
      )}
    </>
  );
};
export default BillableUtilization;
